import { connect } from 'react-redux';
import Checkbox from 'components/common/WishCheckBox';

/* Styled Components */
import {
  PageSubtitle,
  HoursContainer,
  DayName,
  DayHours,
  DayRow,
  OptionalHourLabel,
  Link,
  PageFooter,
  SelectInputWrapper,
} from 'styles/signup/MultiScreenSignupPage';

/* Child Components */
import Button, {
  BUTTON_COLOR_KEYS,
  BUTTON_SIZE_KEYS,
} from 'components/common/Button';
import SectionHeader from 'components/signup/multiscreen/SectionHeader';
import WishSelectInput from 'components/common/WishSelectInput';

/* Actions */
import {
  setIsOpenOnDay as setIsOpenOnDayAction,
  setStoreHours as setStoreHoursAction,
  setStoreStartHour as setStoreStartHourAction,
  setStoreEndHour as setStoreEndHourAction,
  setIsSecondHoursShiftVisible as setIsSecondHoursShiftVisibleAction,
  submitSignupStep as submitSignupStepAction,
  setCurrentSignupStep as setCurrentSignupStepAction,
  setErrorMessage as setErrorMessageAction,
  clearErrorMessage as clearErrorAction,
} from 'app/signupSlice';

/* Selectors */
import {
  getGooglePlacesOpeningHours,
  getDayToIsOpenMap,
  getStoreHours,
  isSecondHoursShiftVisible,
} from 'app/signupSlice';

/* Utils */
import { i18n } from 'utils/i18n';
import {
  DAYS_OF_THE_WEEK,
  SUPPORTED_HOUR_SETS,
  HOURS_SET_0,
  HOURS_SET_1,
  getAPIStoreHours,
} from 'utils/StoreHours';

/* Types */
import type { AppState, AppDispatch } from 'app/store';
import type { OptionsType as SubmitSignupStepOptionsType } from 'services/SubmitSignupStepService';
import type { StoreHours } from 'types/MultiScreenSignupTypes';

/* Constants */
import { SIGNUP_SCREEN } from 'constants/SignupConstants';

const HOUR_LABELS: string[] = [];
const HOUR_VALUES: string[] = [];
for (let i = 0; i < 24; i++) {
  for (let j = 0; j < 2; j++) {
    const hour = i === 0 || i === 12 ? '12' : String(i % 12);
    const minute = String(j * 30).padStart(2, '0');
    const amPm = i >= 12 ? 'PM' : 'AM';
    const hourLabel = `${hour}:${minute}${amPm}`;
    const hourValue = `${String(i).padStart(2, '0')}:${minute}`;
    HOUR_LABELS.push(hourLabel);
    HOUR_VALUES.push(hourValue);
  }
}

const mapStateToProps = (state: AppState) => ({
  googlePlacesHours: getGooglePlacesOpeningHours(state),
  isOpenOnDay: getDayToIsOpenMap(state),
  storeHours: getStoreHours(state),
  isSecondShiftVisible: isSecondHoursShiftVisible(state),
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setIsOpenOnDay: (args: { dayIndex: number; isOpenOnDay: boolean }) =>
    dispatch(setIsOpenOnDayAction(args)),
  setStoreHours: (storeHours: StoreHours) =>
    dispatch(setStoreHoursAction(storeHours)),
  setStoreStartHour: (args: {
    dayIndex: number;
    setOfHoursIndex: number;
    startHour: string;
  }) => dispatch(setStoreStartHourAction(args)),
  setStoreEndHour: (args: {
    dayIndex: number;
    setOfHoursIndex: number;
    endHour: string;
  }) => dispatch(setStoreEndHourAction(args)),
  setIsSecondHoursShiftVisible: (isVisible: boolean) =>
    dispatch(setIsSecondHoursShiftVisibleAction(isVisible)),
  submitSignupStep: (args: {
    signupStep: Values<typeof SIGNUP_SCREEN>;
    nextSignupStep: Values<typeof SIGNUP_SCREEN>;
    options: SubmitSignupStepOptionsType;
  }) => dispatch(submitSignupStepAction(args)),
  setCurrentSignupStep: (signupStep: Values<typeof SIGNUP_SCREEN>) =>
    dispatch(setCurrentSignupStepAction(signupStep)),
  setErrorMessage: (errorMessage: string) =>
    dispatch(setErrorMessageAction(errorMessage)),
  clearError: () => dispatch(clearErrorAction()),
});

type PropsType = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const StoreHoursPage = ({
  googlePlacesHours,
  isOpenOnDay,
  storeHours,
  isSecondShiftVisible,
  setIsOpenOnDay,
  setStoreHours,
  setStoreStartHour,
  setStoreEndHour,
  setIsSecondHoursShiftVisible,
  submitSignupStep,
  setCurrentSignupStep,
  setErrorMessage,
  clearError,
}: PropsType) => {
  const onAddShift = () => {
    clearError();
    setIsSecondHoursShiftVisible(true);
  };

  const onChangeStartTimeDropdown = (
    setOfHoursIndex: number,
    dayIndex: number,
    selectedIndex: number | undefined,
  ) => {
    clearError();
    if (selectedIndex !== null && selectedIndex !== undefined) {
      setStoreStartHour({
        dayIndex,
        setOfHoursIndex,
        startHour: HOUR_VALUES[selectedIndex],
      });
    }
  };

  const onChangeEndTimeDropdown = (
    setOfHoursIndex: number,
    dayIndex: number,
    selectedIndex: number | undefined,
  ) => {
    clearError();
    if (selectedIndex !== null && selectedIndex !== undefined) {
      setStoreEndHour({
        dayIndex,
        setOfHoursIndex,
        endHour: HOUR_VALUES[selectedIndex],
      });
    }
  };

  const getStartTimeSelectedIndex = (
    setOfHoursIndex: number,
    dayIndex: number,
  ) => {
    const hoursForDay = storeHours[dayIndex] || {};
    const hour = hoursForDay[setOfHoursIndex]?.start;
    if (hour !== undefined) {
      return HOUR_VALUES.indexOf(hour);
    }
    return undefined;
  };

  const getEndTimeSelectedIndex = (
    setOfHoursIndex: number,
    dayIndex: number,
  ) => {
    const hoursForDay = storeHours[dayIndex] || {};
    const hour = hoursForDay[setOfHoursIndex]?.end;
    if (hour !== undefined) {
      return HOUR_VALUES.indexOf(hour);
    }
    return undefined;
  };

  const onCheck = (isChecked: boolean, dayIndex: number) => {
    clearError();
    setIsOpenOnDay({ dayIndex, isOpenOnDay: !isOpenOnDay[dayIndex] });
    if (!isChecked) {
      const updatedStoreHours = { ...storeHours };
      updatedStoreHours[dayIndex] = {
        [HOURS_SET_0]: { start: '', end: '' },
        [HOURS_SET_1]: { start: '', end: '' },
      };
      setStoreHours(updatedStoreHours);
    }
  };

  const validateForm = () => {
    let hasHours = false;
    for (let i = 0; i < DAYS_OF_THE_WEEK.length; i++) {
      const day = DAYS_OF_THE_WEEK[i];
      const dayIndex = day.value;
      if (isOpenOnDay[dayIndex]) {
        const hoursForDay = storeHours[dayIndex] || {};

        for (
          let setOfHoursIndex = 0;
          setOfHoursIndex < SUPPORTED_HOUR_SETS;
          setOfHoursIndex++
        ) {
          const hours = hoursForDay[setOfHoursIndex] || {};
          const startHour = hours.start || '';
          const endHour = hours.end || '';

          // Only first set of hours for each day of the week is required.
          // We only need to check the other sets of hours if a start or end time was specified.
          if (setOfHoursIndex === 0 || startHour || endHour) {
            const selectedStartIndex = HOUR_VALUES.indexOf(startHour);
            const selectedEndIndex = HOUR_VALUES.indexOf(endHour);
            if (selectedStartIndex === -1 || selectedEndIndex === -1) {
              return i18n('Both start and end time are required');
            }
            if (setOfHoursIndex > 0) {
              const previousHours = hoursForDay[setOfHoursIndex - 1] || {};
              const previousEndHour = previousHours.end || '';
              if (startHour <= previousEndHour) {
                return i18n(
                  'The second time range should start after the first one ends',
                );
              }
            }
            if (selectedEndIndex <= selectedStartIndex) {
              // Can't have end times before or equal start times
              return i18n('End time must be after start time');
            }
            hasHours = true;
          }
        }
      }
    }
    if (!hasHours) {
      return i18n('Please enter your business hours');
    }
    return undefined;
  };

  const hasAutocompletedHours = Boolean(googlePlacesHours);

  const onSubmit = () => {
    clearError();
    const error = validateForm();
    if (error) {
      setErrorMessage(error);
      return;
    }
    const hoursList = getAPIStoreHours(storeHours);
    submitSignupStep({
      signupStep: SIGNUP_SCREEN.STORE_HOURS,
      nextSignupStep: SIGNUP_SCREEN.STORE_CATEGORY,
      options: {
        hoursList,
      },
    });
  };

  return (
    <>
      <SectionHeader
        title={
          hasAutocompletedHours
            ? i18n('Confirm your business hours')
            : i18n('Set business hours')
        }
        onBackClicked={() => {
          clearError();
          setCurrentSignupStep(SIGNUP_SCREEN.STORE_ADDRESS);
        }}
      />
      <PageSubtitle center>
        {hasAutocompletedHours
          ? i18n(
              'We fetched these hours for your location online. Please make sure they’re correct before continuing.',
            )
          : i18n(
              'Please make sure these hours are accurate and match other websites.',
            )}
      </PageSubtitle>

      {!isSecondShiftVisible && (
        <PageSubtitle center>
          {i18n('Open for two time periods a day?')}
          <Link onClick={onAddShift}>{i18n('Add a shift')}</Link>
        </PageSubtitle>
      )}

      <HoursContainer>
        {DAYS_OF_THE_WEEK.map(({ value, name }) => (
          <DayHours key={value}>
            <DayRow key={HOURS_SET_0}>
              <Checkbox
                onCheck={isChecked => onCheck(isChecked, value)}
                isChecked={isOpenOnDay[value]}
              />
              <DayName>{name()}</DayName>

              <SelectInputWrapper>
                <WishSelectInput
                  items={HOUR_LABELS}
                  onChange={selectedIndex =>
                    onChangeStartTimeDropdown(HOURS_SET_0, value, selectedIndex)
                  }
                  selectedIndex={getStartTimeSelectedIndex(HOURS_SET_0, value)}
                  styles={{ margin: '0', boxPadding: '0 0 0 8px' }}
                  dropdownItemStyle={{
                    justifyContent: 'left',
                    padding: '0 12px',
                  }}
                  disabled={!isOpenOnDay[value]}
                />
              </SelectInputWrapper>

              <div>-</div>

              <SelectInputWrapper>
                <WishSelectInput
                  items={HOUR_LABELS}
                  onChange={selectedIndex =>
                    onChangeEndTimeDropdown(HOURS_SET_0, value, selectedIndex)
                  }
                  selectedIndex={getEndTimeSelectedIndex(HOURS_SET_0, value)}
                  styles={{ margin: '0', boxPadding: '0 0 0 8px' }}
                  dropdownItemStyle={{
                    justifyContent: 'left',
                    padding: '0 12px',
                  }}
                  disabled={!isOpenOnDay[value]}
                />
              </SelectInputWrapper>
            </DayRow>

            {isSecondShiftVisible && (
              <DayRow key={HOURS_SET_1}>
                <span />
                <OptionalHourLabel>{i18n('Optional')}</OptionalHourLabel>
                <SelectInputWrapper>
                  <WishSelectInput
                    items={HOUR_LABELS}
                    onChange={selectedIndex =>
                      onChangeStartTimeDropdown(
                        HOURS_SET_1,
                        value,
                        selectedIndex,
                      )
                    }
                    selectedIndex={getStartTimeSelectedIndex(
                      HOURS_SET_1,
                      value,
                    )}
                    styles={{ margin: '0', boxPadding: '0 0 0 8px' }}
                    dropdownItemStyle={{
                      justifyContent: 'left',
                      padding: '0 12px',
                    }}
                    disabled={!isOpenOnDay[value]}
                  />
                </SelectInputWrapper>

                <div>-</div>
                <SelectInputWrapper>
                  <WishSelectInput
                    items={HOUR_LABELS}
                    onChange={selectedIndex =>
                      onChangeEndTimeDropdown(HOURS_SET_1, value, selectedIndex)
                    }
                    selectedIndex={getEndTimeSelectedIndex(HOURS_SET_1, value)}
                    styles={{ margin: '0', boxPadding: '0 0 0 8px' }}
                    dropdownItemStyle={{
                      justifyContent: 'left',
                      padding: '0 12px',
                    }}
                    disabled={!isOpenOnDay[value]}
                  />
                </SelectInputWrapper>
              </DayRow>
            )}
          </DayHours>
        ))}
      </HoursContainer>

      <PageFooter>
        <Button
          onClick={onSubmit}
          size={BUTTON_SIZE_KEYS.MEDIUM}
          color={BUTTON_COLOR_KEYS.DARK_COBALT}
          stretch
          bold
          overrideStyle={{
            margin: '24px 0 0 0',
          }}
        >
          {i18n('Continue')}
        </Button>
      </PageFooter>
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(StoreHoursPage);
